import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ArrowLeft,
  Archive,
  FileText,
  Receipt,
  Trash2,
  Plus,
  Copy,
  Pencil,
  History,
  AlertTriangle,
  Calendar,
  ExternalLink,
  Upload,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Link2,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Wrench,
  GripVertical,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { useApp } from '@/store';
import { onSiteExtinguishers, HOSE_COUNT_KEY } from '@/lib/device-counts';
import { SectionDialog } from '@/components/dialogs/SectionDialog';
import * as sections from '@/lib/sections';
import { CONDITION_LABEL_KEYS, isConditionFault } from '@/lib/hose-condition';
import { deviceTypeLabel, resolveDeviceCounts } from '@/lib/device-type-label';
import { useT, HOSE_COND_LABEL_KEYS, BILLING_LABEL_KEYS } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import {
  deriveLastInspection,
  deriveNextInspection,
} from '@/lib/customer-dates';
import {
  signAndPut,
  resolveUploadUrl,
  UploadKind,
  UploadFailedError,
} from '@/lib/uploads';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { ScrollTable } from '@/components/layout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AddressDialog } from '@/components/dialogs/AddressDialog';
import {
  ContactDialog,
  contactRoleLabel,
} from '@/components/dialogs/ContactDialog';
import { geocode } from '@/lib/geocode';
import { getDecoded } from '@/lib/auth';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { useConfirm } from '@/components/ConfirmDialog';
import { CompleteInspectionDialog } from './CompleteInspectionDialog';
import {
  Billing,
  ConditionValue,
  VisitStatus,
  type Address,
  type ArchiveDevice,
  type Archive as ArchiveRecord,
  type Contact,
  type Customer,
  type Extinguisher,
  type Hose,
  type HoseCondition,
  type Visit,
  SectionColor,
} from '@/data/types';

type AddressKey = 'billingAddress' | 'objectAddress' | 'ownerAddress'; // enum-ignore

type ExtSortKey =
  | 'floor'
  | 'location'
  | 'mfrModel'
  | 'type'
  | 'agent'
  | 'exactAgent'
  | 'id'
  | 'year'
  | 'last'
  | 'next'
  | 'refilled'
  | 'billing'
  | 'status';

export function CustomerDetail() {
  const { id, tab } = useParams();
  const nav = useNavigate();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const allCustomers = useApp(s => s.customers);
  const toast = useApp(s => s.toast);
  const deleteCustomer = useApp(s => s.deleteCustomer);
  const updateCustomer = useApp(s => s.updateCustomer);
  const createArchive = useApp(s => s.createArchive);
  const deleteExtinguisher = useApp(s => s.deleteExtinguisher);
  const batchUpdateInspection = useApp(s => s.batchUpdateInspection);
  const deleteHose = useApp(s => s.deleteHose);
  const upsertVisit = useApp(s => s.upsertVisit);
  const setMaintained = useApp(s => s.setMaintained);
  const addCustomer = useApp(s => s.addCustomer);
  const workers = useApp(s => s.workers);
  const types = useApp(s => s.deviceTypes);
  const lang = useApp(s => s.lang);
  const manufacturers = useApp(s => s.manufacturers);
  const inspections = useApp(s => s.inspections) ?? [];

  const t = useT();
  const confirm = useConfirm();
  // Section rights (client #95): what this user may see and change here. The
  // store guard enforces the same rules; hiding the controls just keeps the
  // screen honest about it.
  const rights = useSectionRights();
  const canCust = rights[SectionId.CUSTOMERS];
  const canExt = rights[SectionId.EXTINGUISHERS];
  const canHose = rights[SectionId.HOSES];
  const canMaint = rights[SectionId.MAINTENANCE];
  const canInvoice = rights[SectionId.INVOICES].read;

  const agentTypeOf = (e: Extinguisher) => {
    const mfr = manufacturers.find(m => m.name === e.mfr);
    return mfr?.models.find(mo => mo.name === e.model)?.agentType ?? '';
  };
  const [editing, setEditing] = useState<AddressKey | null>(null);
  const contractInputRef = useRef<HTMLInputElement>(null);
  const notesFileRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const openUpload = async (stored: string) => {
    try {
      window.open(await resolveUploadUrl(stored), '_blank', 'noopener');
    } catch {
      toast(t('common.error'));
    }
  };
  const [sortKey, setSortKey] = useState<ExtSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [contactEdit, setContactEdit] = useState<{
    index: number | null;
  } | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  // Which visit's maintenance dialog is open is store state, not component
  // state: adding a device from inside the dialog routes away from this page,
  // and the maintenance has to still be there on the way back (client #64).
  const activeVisitId = useApp(s => s.activeVisitId);
  const setActiveVisit = useApp(s => s.setActiveVisit);
  const visits = useApp(s => s.visits) ?? [];
  const completing =
    visits.find(v => v.id === activeVisitId && v.customerId === id) ?? null;
  const [dragId, setDragId] = useState<string | null>(null);
  // Section divider editing (client #30): either an existing section name being
  // edited, or the row index a new divider is being inserted above.
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [insertAt, setInsertAt] = useState<number | null>(null);

  // Divider rules live in lib/sections so the tests exercise the same code the
  // screen does (client #30).
  const nextSectionAnchor = () =>
    sections.nextSectionAnchor(customer?.extinguishers ?? []);

  const moveSection = (name: string, dir: -1 | 1) => {
    if (!customer) return;
    updateCustomer(customer.id, {
      extinguishers: sections.moveSection(customer.extinguishers, name, dir),
    });
  };

  const insertSection = (index: number, name: string, color: SectionColor) => {
    if (!customer) return;
    updateCustomer(customer.id, {
      extinguishers: sections.insertSection(
        customer.extinguishers,
        index,
        name
      ),
      sectionColors: { ...(customer.sectionColors ?? {}), [name]: color },
    });
    setInsertAt(null);
  };

  const renameSection = (from: string, to: string, color: SectionColor) => {
    if (!customer) return;
    const colors = { ...(customer.sectionColors ?? {}) };
    if (from !== to) delete colors[from];
    colors[to] = color;
    updateCustomer(customer.id, {
      extinguishers: sections.renameSection(customer.extinguishers, from, to),
      sectionColors: colors,
    });
    setEditingSection(null);
  };

  const removeSection = (name: string) => {
    if (!customer) return;
    const colors = { ...(customer.sectionColors ?? {}) };
    delete colors[name];
    updateCustomer(customer.id, {
      extinguishers: sections.removeSection(customer.extinguishers, name),
      sectionColors: colors,
    });
    setEditingSection(null);
  };
  const [archiveOpen, setArchiveOpen] = useState<ArchiveRecord | null>(null);

  // Manual drag-and-drop ordering of extinguishers (client 17 July — walk the
  // building in order). Persists the array order; only active in the default
  // (non-column-sorted) view.
  const reorderExtinguisher = (fromId: string, toId: string) => {
    if (!customer || fromId === toId) return;
    const arr = [...customer.extinguishers];
    const from = arr.findIndex(x => x.id === fromId);
    const to = arr.findIndex(x => x.id === toId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    updateCustomer(customer.id, { extinguishers: arr });
  };

  // Same manual ordering for hoses so a walk-through covers both device lists
  // in one pass (client #84). Order is array position, as with extinguishers.
  const reorderHose = (fromId: string, toId: string) => {
    if (!customer || fromId === toId) return;
    const arr = [...customer.hoses];
    const from = arr.findIndex(x => x.id === fromId);
    const to = arr.findIndex(x => x.id === toId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    updateCustomer(customer.id, { hoses: arr });
  };

  // Apply scrolling to both candidates instead of guessing which one owns it.
  // <main> carries overflow-y:auto but usually grows to its full content height
  // so the page scrolls instead — and which of the two is live changes during
  // mount, while rows are still arriving. Writing scrollTop on an element that
  // cannot scroll is a harmless no-op, so setting both is strictly more robust
  // than detecting (three attempts at detection all missed — clients #41, #29).
  const scrollTargets = (): HTMLElement[] => {
    const out: HTMLElement[] = [];
    const main = document.querySelector('main');
    if (main) out.push(main as HTMLElement);
    const page = (document.scrollingElement ??
      document.documentElement) as HTMLElement | null;
    if (page) out.push(page);
    return out;
  };
  const currentScrollTop = (): number =>
    Math.max(...scrollTargets().map(el => el.scrollTop), 0);

  // Dragging a device to the top of a long list needs the page to scroll
  // itself — HTML5 drag-and-drop does not do that, so a device could not be
  // moved further than one screen (client #29).
  const edgeAutoScroll = (clientY: number) => {
    const EDGE = 80;
    const STEP = 18;
    const delta =
      clientY < EDGE ? -STEP : clientY > window.innerHeight - EDGE ? STEP : 0;
    if (!delta) return;
    for (const el of scrollTargets()) el.scrollTop += delta;
  };

  // The notes field is uncontrolled so that typing is never interrupted by a
  // re-render, but that also meant a note edited on another device replicated
  // into the store and never reached the textarea — it kept showing the stale
  // text until a reload (the same complaint as client #68, in one field).
  // Adopt the incoming value, but only while the field is not focused, so a
  // replication landing mid-sentence cannot wipe what is being typed.
  useEffect(() => {
    const el = notesRef.current;
    if (!el || document.activeElement === el) return;
    const incoming = customer?.notes ?? '';
    if (el.value !== incoming) el.value = incoming;
  }, [customer?.notes]);

  // Restore the list scroll position when returning from editing a device
  // (client #41 — the viewport jumped back to the top). Persist it per
  // customer + tab against whichever element actually scrolls.
  useEffect(() => {
    if (tab !== 'extinguishers' && tab !== 'hoses') return;
    const key = `listScroll:${id}:${tab}`;
    const saved = Number(sessionStorage.getItem(key) ?? 0);
    // Restoring in a single frame silently failed: the rows have not been laid
    // out yet, so the container is still short and scrollTop clamps back. The
    // big registers render incrementally (361 devices on the largest customer),
    // which took longer than a frame-counted budget — so keep re-applying until
    // it sticks or the deadline passes, and give up the moment the technician
    // scrolls themselves rather than fighting them.
    let restoring = saved > 0;
    let raf = 0;
    const deadline = performance.now() + 6000;
    const stop = () => {
      restoring = false;
      cancelAnimationFrame(raf);
    };
    // Keep re-applying for the whole window rather than stopping at the first
    // success: the offset lands, and then something later in the return
    // navigation puts it back (measured as a consistent drop to the edit
    // page's own 118). Pinning outlives that; real input releases it at once
    // through stop().
    const restore = () => {
      if (!restoring) return;
      for (const el of scrollTargets()) el.scrollTop = saved;
      if (performance.now() < deadline) {
        raf = requestAnimationFrame(restore);
      } else {
        restoring = false;
      }
    };
    if (restoring) raf = requestAnimationFrame(restore);
    const onScroll = () => {
      // Ignore the clamped values emitted while we are still restoring.
      const top = currentScrollTop();
      if (restoring && top !== saved) return;
      sessionStorage.setItem(key, String(top));
    };
    // Capture on window: scroll does not bubble, so this is the only listener
    // that sees it whether the page or an inner container is the scroller.
    window.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });
    for (const ev of ['wheel', 'touchstart', 'keydown'] as const) {
      window.addEventListener(ev, stop, { passive: true, once: true });
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      for (const ev of ['wheel', 'touchstart', 'keydown'] as const) {
        window.removeEventListener(ev, stop);
      }
    };
  }, [tab, id]);

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('customer.notFound')}</p>
        <Button variant="link" onClick={() => nav('/overview')}>
          {t('common.backToOverview')}
        </Button>
      </div>
    );
  }

  const handleDelete = async () => {
    if (await confirm(`${t('customer.deleteConfirm')} ${customer.name}?`)) {
      deleteCustomer(customer.id);
      toast(`${t('customer.deleted')} ${customer.name}`);
      nav('/overview');
    }
  };

  const handleArchive = () => {
    createArchive(customer.id);
    toast(t('customer.archiveCreated'));
  };

  // Duplicate a customer (client #74) — sites in the same group are set up
  // nearly identically, so the addresses, contacts and equipment list are
  // worth copying. Everything that records work actually done (inspection
  // dates, archives, signatures) starts empty on the copy.
  const handleDuplicate = async () => {
    // Confirm first: the button sits between "Wartung starten" and "Löschen",
    // and a misclick silently created a full copy of the site — devices and
    // all — that someone then had to find and delete (client #96).
    if (
      !(await confirm(`${t('customer.duplicateConfirm')} ${customer.name}?`))
    ) {
      return;
    }
    const now = Date.now();
    const copy: Customer = {
      ...customer,
      id: `C-${now}`,
      name: `${customer.name} (${t('common.copy')})`,
      created: new Date().toISOString().slice(0, 10),
      // The copy is the copier's own customer (server scope, client #92).
      createdBy: undefined,
      lastInspection: undefined,
      nextInspection: undefined,
      archives: [],
      linkedCustomerIds: undefined,
      extinguishers: customer.extinguishers.map((e, i) => ({
        ...e,
        id: `E-${now}-${i}`,
        deviceNumber: '',
        lastInspection: undefined,
        nextInspection: undefined,
        refilled: undefined,
        // Same rule as copying a single device: parts belong to the work done,
        // not to the equipment being duplicated (client #86).
        spareParts: [],
        photos: undefined,
      })),
      hoses: customer.hoses.map((h, i) => ({
        ...h,
        id: `H-${now}-${i}`,
        deviceNumber: undefined,
        lastService: undefined,
        nextService: undefined,
        spareParts: [],
        photos: undefined,
      })),
    };
    addCustomer(copy);
    toast(t('customer.copied'));
    nav(`/customer/${copy.id}`);
  };

  // Walk-in maintenance: create a today-visit and open the inspection dialog
  // directly, skipping the calendar (client 16 July).
  const startMaintenance = () => {
    const d = getDecoded();
    const today = new Date().toISOString().slice(0, 10);
    // Reopening must resume the visit already in progress, not mint a new one.
    // The inspection draft is keyed by visit id, so a fresh id orphans every
    // reading and spare part the technician already entered — the work is still
    // on disk, but the form comes back blank, which is exactly how the client
    // described #64 ("all the information is discarded").
    const inProgress = visits.find(
      v =>
        v.customerId === customer.id &&
        v.status === VisitStatus.PLANNED &&
        v.date === today
    );
    if (inProgress) {
      setActiveVisit(inProgress.id);
      return;
    }
    // Whoever starts the maintenance does it: their login, and their name as
    // Mitarbeiter when they have a worker record, so the visit's two fields
    // agree (client #92). Without one the customer's Mitarbeiter stays.
    const email = d?.email ?? d?.sub;
    const me = workers.find(
      w => !!email && w.email?.toLowerCase() === email.toLowerCase()
    );
    const visit: Visit = {
      id: `V-${Date.now()}`,
      customerId: customer.id,
      date: new Date().toISOString().slice(0, 10),
      worker: me?.name ?? customer.worker,
      assignedTechId: email,
      status: VisitStatus.PLANNED,
    };
    upsertVisit(visit, SectionId.MAINTENANCE);
    setActiveVisit(visit.id);
  };

  const handleBatchInspect = () => {
    batchUpdateInspection(customer.id);
    toast(t('customer.batchInspectDone'));
  };

  const saveAddress = (key: AddressKey) => async (a: Address) => {
    const patch: Partial<typeof customer> = { [key]: a };
    if (key === 'objectAddress') {
      const g = await geocode(a);
      if (g) {
        patch.lat = g.lat;
        patch.lng = g.lng;
        toast(t('customer.coordsResolved'));
      }
    }
    updateCustomer(customer.id, patch);
    toast(t('customer.addressSaved'));
  };

  // A copy belongs next to its original — the list is walked in building order,
  // so appending it to the very bottom put it nowhere near the device it was
  // copied from (client #66).
  const insertAfter = <T extends { id: string }>(arr: T[], src: T, copy: T) => {
    const next = [...arr];
    const at = next.findIndex(x => x.id === src.id);
    next.splice(at < 0 ? next.length : at + 1, 0, copy);
    return next;
  };

  const copyExtinguisher = (e: Extinguisher) => {
    const copy: Extinguisher = {
      ...e,
      id: `E-${Date.now()}`,
      deviceNumber: '',
      // Spare parts belong to the work done on one device, not to the device
      // template being duplicated — copying them made the next maintenance
      // start with parts nobody fitted (client #86).
      spareParts: [],
    };
    updateCustomer(customer.id, {
      extinguishers: insertAfter(customer.extinguishers, e, copy),
    });
    toast(t('customer.copied'));
  };

  // Disposal with replacement: the old unit is billed as disposed and stays on
  // the list as a record, and its replacement takes the same spot in the walk
  // order, billed as new (client #54).
  const replaceExtinguisher = (e: Extinguisher) => {
    const replacement: Extinguisher = {
      ...e,
      id: `E-${Date.now()}`,
      billing: Billing.NEW,
      year: new Date().getFullYear(),
      lastInspection: undefined,
      nextInspection: undefined,
      refilled: undefined,
      spareParts: [],
      photos: undefined,
    };
    const next = insertAfter(customer.extinguishers, e, replacement).map(x =>
      x.id === e.id ? { ...x, billing: Billing.DISPOSED } : x
    );
    updateCustomer(customer.id, { extinguishers: next });
    toast(t('ext.replaceDone'));
  };

  const copyHose = (h: Hose) => {
    const copy: Hose = {
      ...h,
      id: `H-${Date.now()}`,
      deviceNumber: undefined,
      // Parity with copyExtinguisher — a duplicate is a piece of equipment,
      // not a record of work done on one (client #86).
      spareParts: [],
    };
    updateCustomer(customer.id, {
      hoses: insertAfter(customer.hoses, h, copy),
    });
    toast(t('customer.copied'));
  };

  const saveName = () => {
    const next = nameDraft.trim();
    if (next && next !== customer.name) {
      updateCustomer(customer.id, { name: next });
      toast(t('customer.addressSaved'));
    }
    setEditingName(false);
  };

  const removeExtinguisher = async (e: Extinguisher) => {
    if (
      !(await confirm(
        `${t('common.delete')} ${t('parts.extinguisher')} ${e.deviceNumber ?? e.id}?`
      ))
    )
      return;
    deleteExtinguisher(customer.id, e.id);
    toast(t('customer.deleted'));
  };

  const ageOf = (e: Extinguisher) => {
    if (!e.year) return null;
    const dt = types.find(x => x.id === e.type);
    if (!dt) return null;
    const age = new Date().getFullYear() - e.year;
    if (age > dt.ageLimit)
      return { age, limit: dt.ageLimit, cls: dt.pressureClass };
    return null;
  };

  const deviceCounts = Object.entries(
    resolveDeviceCounts(customer.deviceCounts, types)
  );
  // Disposed units remain in the array as a service record but are off site, so
  // every visible count excludes them (client #77).
  const onSiteExts = onSiteExtinguishers(customer.extinguishers);

  const isMaintainedThisCycle = (e: Extinguisher) => {
    if (e.maintainedThisCycle !== undefined) return e.maintainedThisCycle;
    if (!e.lastInspection || !customer.lastInspection) return false;
    return e.lastInspection >= customer.lastInspection;
  };

  const toggleMaintained = (e: Extinguisher) =>
    setMaintained(customer.id, e.id, !isMaintainedThisCycle(e));

  const toggleSort = (key: ExtSortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortValue = (e: Extinguisher, key: ExtSortKey): string | number => {
    switch (key) {
      case 'floor':
        return e.floor ?? '';
      case 'location':
        return e.location ?? '';
      case 'mfrModel':
        return `${e.mfr ?? ''} ${e.model ?? ''}`;
      case 'type':
        return e.type ?? '';
      case 'id':
        return e.deviceNumber ?? '';
      case 'year':
        return e.year ?? 0;
      case 'last':
        return e.lastInspection ?? '';
      case 'next':
        return e.nextInspection ?? '';
      case 'refilled':
        return e.refilled ?? '';
      case 'billing':
        return e.billing ?? '';
      case 'agent':
        return agentTypeOf(e);
      case 'exactAgent':
        return e.content ?? '';
      case 'status':
        return isMaintainedThisCycle(e) ? 1 : 0;
    }
  };

  const SortHeader = ({
    sortKeyProp,
    children,
    className,
  }: {
    sortKeyProp: ExtSortKey;
    children: React.ReactNode;
    className?: string;
  }) => {
    const active = sortKey === sortKeyProp;
    const Icon = !active
      ? ArrowUpDown
      : sortDir === 'asc'
        ? ArrowUp
        : ArrowDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => toggleSort(sortKeyProp)}
          className={`inline-flex items-center gap-1 hover:text-foreground ${
            active ? 'text-primary font-semibold' : ''
          }`}
        >
          {children}
          <Icon className="h-3 w-3" />
        </button>
      </TableHead>
    );
  };

  const linkedCustomers = (customer.linkedCustomerIds ?? [])
    .map(lid => allCustomers.find(c => c.id === lid))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  const linkCandidates = allCustomers
    .filter(c => c.id !== customer.id)
    .filter(c => !(customer.linkedCustomerIds ?? []).includes(c.id))
    .filter(c =>
      linkQuery
        ? `${c.name} ${c.id}`.toLowerCase().includes(linkQuery.toLowerCase())
        : true
    )
    .slice(0, 20);

  const addLinkedCustomer = (otherId: string) => {
    const myIds = customer.linkedCustomerIds ?? [];
    if (myIds.includes(otherId)) return;
    updateCustomer(customer.id, {
      linkedCustomerIds: [...myIds, otherId],
    });
    const other = allCustomers.find(c => c.id === otherId);
    if (other) {
      const otherIds = other.linkedCustomerIds ?? [];
      if (!otherIds.includes(customer.id)) {
        updateCustomer(otherId, {
          linkedCustomerIds: [...otherIds, customer.id],
        });
      }
    }
    setLinkQuery('');
    setLinkPickerOpen(false);
  };

  const removeLinkedCustomer = (otherId: string) => {
    updateCustomer(customer.id, {
      linkedCustomerIds: (customer.linkedCustomerIds ?? []).filter(
        x => x !== otherId
      ),
    });
    const other = allCustomers.find(c => c.id === otherId);
    if (other) {
      updateCustomer(otherId, {
        linkedCustomerIds: (other.linkedCustomerIds ?? []).filter(
          x => x !== customer.id
        ),
      });
    }
  };

  const addressCards: { title: string; addr?: Address; key: AddressKey }[] = [
    {
      title: t('customer.billingAddress'),
      addr: customer.billingAddress,
      key: 'billingAddress',
    },
    {
      title: t('customer.objectAddress'),
      addr: customer.objectAddress,
      key: 'objectAddress',
    },
    {
      title: t('customer.ownerAddress'),
      addr: customer.ownerAddress,
      key: 'ownerAddress',
    },
  ];

  const saveContact = (index: number | null) => (c: Contact) => {
    const list = [...(customer.contacts ?? [])];
    if (index === null) list.push(c);
    else list[index] = c;
    updateCustomer(customer.id, { contacts: list });
    toast(t('customer.addressSaved'));
  };

  // A device tab the user may not read falls back to the info tab (#95).
  const tabValue =
    (tab === 'extinguishers' && !canExt.read) ||
    (tab === 'hoses' && !canHose.read)
      ? 'info'
      : (tab ?? 'info');

  const removeContact = async (index: number) => {
    const c = (customer.contacts ?? [])[index];
    const label = c?.name || c?.customRole || t('customer.contacts');
    // Deleting a contact was immediate and unrecoverable (client #85).
    if (!(await confirm(`${t('common.delete')} ${label}?`))) return;
    const list = (customer.contacts ?? []).filter((_, i) => i !== index);
    updateCustomer(customer.id, { contacts: list });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-tour="customer-detail"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/overview"
            className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" /> {t('common.backToOverview')}
          </Link>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {editingName ? (
              <div className="flex items-center gap-1">
                <Input
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  className="h-9 text-lg font-bold"
                  autoFocus
                  data-testid="customer-name-input"
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={saveName}
                  title={t('common.save')}
                  aria-label={t('common.save')}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setEditingName(false)}
                  title={t('common.cancel')}
                  aria-label={t('common.cancel')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : canCust.edit ? (
              <button
                type="button"
                className="group inline-flex items-center gap-2"
                onClick={() => {
                  setNameDraft(customer.name);
                  setEditingName(true);
                }}
                title={t('common.edit')}
                data-testid="customer-name-edit"
              >
                <h1 className="text-2xl font-bold tracking-tight">
                  {customer.name}
                </h1>
                <Pencil className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ) : (
              <h1 className="text-2xl font-bold tracking-tight">
                {customer.name}
              </h1>
            )}
            <StatusBadge status={customer.status} />
            <Badge variant="outline" className="font-mono">
              {customer.id}
            </Badge>
            <Badge>
              {onSiteExts.length + customer.hoses.length}{' '}
              {t('customer.devices')}
            </Badge>
            {customer.hasLinkedFiles && (
              <Badge variant="secondary" className="gap-1">
                <Paperclip className="h-3 w-3" />
                {t('customer.linkedFiles')}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canMaint.create && (
            <Button size="sm" onClick={startMaintenance}>
              <Wrench className="h-4 w-4" />
              {t('customer.startMaintenance')}
            </Button>
          )}
          {canCust.create && (
            <Button variant="outline" size="sm" onClick={handleDuplicate}>
              <Copy className="h-4 w-4" />
              {t('customer.duplicate')}
            </Button>
          )}
          {canCust.edit && (
            <Button variant="outline" size="sm" onClick={handleArchive}>
              <Archive className="h-4 w-4" />
              {t('customer.archive')}
            </Button>
          )}
          {canExt.read && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/customer/${customer.id}/extinguisher-report`}>
                <FileText className="h-4 w-4" />
                {t('extReport.title')}
              </Link>
            </Button>
          )}
          {canHose.read && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/customer/${customer.id}/hose-report`}>
                <FileText className="h-4 w-4" />
                {t('hoseReport.title')}
              </Link>
            </Button>
          )}
          {canCust.delete && (
            <Button
              variant="destructive"
              size="sm"
              data-testid="customer-delete"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" />
              {t('common.delete')}
            </Button>
          )}
        </div>
      </div>

      <Tabs
        value={tabValue}
        onValueChange={v => nav(`/customer/${customer.id}/${v}`)}
      >
        <TabsList className="w-full overflow-x-auto justify-start sm:justify-center">
          <TabsTrigger value="info">{t('customer.info')}</TabsTrigger>
          {/* Counts sit on the tab itself so the technician sees how much is on
              site without opening each list (client #22). */}
          {canExt.read && (
            <TabsTrigger value="extinguishers" className="gap-1.5">
              {t('customer.extinguishers')}
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {onSiteExts.length}
              </Badge>
            </TabsTrigger>
          )}
          {canHose.read && (
            <TabsTrigger value="hoses" className="gap-1.5">
              {t('customer.hoses')}
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {customer.hoses.length}
              </Badge>
            </TabsTrigger>
          )}
          <TabsTrigger value="history">{t('customer.history')}</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {addressCards.map(x => (
              <Card key={x.title}>
                <CardHeader>
                  <CardTitle className="text-sm">{x.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  {x.addr ? (
                    <>
                      {x.addr.company && (
                        <div className="font-medium">{x.addr.company}</div>
                      )}
                      {x.addr.attn && (
                        <div className="text-muted-foreground">
                          {x.addr.attn}
                        </div>
                      )}
                      {x.addr.street && (
                        <div className="text-muted-foreground">
                          {x.addr.street}
                        </div>
                      )}
                      {(x.addr.zip || x.addr.city) && (
                        <div className="text-muted-foreground">
                          {x.addr.zip} {x.addr.city}
                        </div>
                      )}
                      {(x.addr.phones ?? []).map((p, i) => (
                        <div key={i} className="text-muted-foreground">
                          {t('customer.tel')} {p}
                        </div>
                      ))}
                      {x.addr.email && (
                        <div className="text-muted-foreground">
                          {x.addr.email}
                        </div>
                      )}
                      {canCust.edit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2"
                          data-testid={`addr-edit-${x.key}`}
                          onClick={() => setEditing(x.key)}
                        >
                          <Pencil className="h-3 w-3" />
                          {t('common.edit')}
                        </Button>
                      )}
                    </>
                  ) : canCust.edit ? (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`addr-edit-${x.key}`}
                      onClick={() => setEditing(x.key)}
                    >
                      <Plus className="h-3 w-3" />
                      {t('common.add')}
                    </Button>
                  ) : (
                    <div className="text-muted-foreground">
                      {t('common.notSet')}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {t('customer.contacts')}
                  </CardTitle>
                  {canCust.edit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setContactEdit({ index: null })}
                    >
                      <Plus className="h-3 w-3" />
                      {t('common.add')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {(customer.contacts ?? []).length === 0 && (
                  <div className="text-muted-foreground">
                    {t('common.notSet')}
                  </div>
                )}
                {(customer.contacts ?? []).map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-2"
                  >
                    <div>
                      <div className="font-medium">
                        {contactRoleLabel(c, t)}: {c.name}
                      </div>
                      <div className="text-muted-foreground">{c.phone}</div>
                      {c.email && (
                        <div className="text-muted-foreground">{c.email}</div>
                      )}
                    </div>
                    {canCust.edit && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                          onClick={() => setContactEdit({ index: i })}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                          onClick={() => removeContact(i)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-focus="customer-dates">
              <CardHeader>
                <CardTitle className="text-sm">
                  {t('customer.metadata')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.created')}
                  </span>
                  <span>{fmtDate(customer.created)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.createdBy')}
                  </span>
                  <span>{customer.createdBy ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.worker')}
                  </span>
                  <span>{customer.worker ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.lastInspection')}
                  </span>
                  <span>{fmtDate(deriveLastInspection(customer))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.nextInspection')}
                  </span>
                  <span>{fmtDate(deriveNextInspection(customer))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.coordinates')}
                  </span>
                  {customer.lat != null && customer.lng != null ? (
                    <a
                      href={`https://www.google.com/maps?q=${customer.lat},${customer.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {customer.lat.toFixed(5)}, {customer.lng.toFixed(5)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono text-xs">—</span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card data-focus="service-contract">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {t('customer.serviceContract')}
                  </CardTitle>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm">
                      {customer.serviceContract
                        ? t('common.yes')
                        : t('common.no')}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={customer.serviceContract ?? false}
                      disabled={!canCust.edit}
                      onClick={() =>
                        updateCustomer(customer.id, {
                          serviceContract: !(customer.serviceContract ?? false),
                        })
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        customer.serviceContract ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg ring-0 transition-transform ${
                          customer.serviceContract
                            ? 'translate-x-5'
                            : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </label>
                </div>
              </CardHeader>
              {customer.serviceContract && (
                <CardContent className="space-y-2">
                  <input
                    ref={contractInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      // A failed upload now rejects instead of quietly storing
                      // a multi-megabyte data: URL (client #48) — say so.
                      try {
                        const stored = await signAndPut(file, UploadKind.PDF);
                        updateCustomer(customer.id, {
                          serviceContractPdf: stored,
                        });
                        toast(t('customer.contractUploaded'));
                      } catch (err) {
                        toast(
                          err instanceof UploadFailedError
                            ? t(err.i18nKey)
                            : t('common.error')
                        );
                      }
                      e.target.value = '';
                    }}
                  />
                  <div className="flex gap-2 flex-wrap">
                    {canCust.edit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => contractInputRef.current?.click()}
                      >
                        <Upload className="h-3 w-3" />
                        {t('customer.uploadContract')}
                      </Button>
                    )}
                    {customer.serviceContractPdf && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openUpload(customer.serviceContractPdf!)}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {t('customer.viewContract')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            <Card data-focus="linked-customers">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-sm">
                    {t('customer.linkedCustomers')}
                  </CardTitle>
                  {canCust.edit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLinkPickerOpen(v => !v)}
                    >
                      <Link2 className="h-3 w-3" />
                      {t('customer.linkCustomer')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {linkPickerOpen && canCust.edit && (
                  <div className="space-y-1 border rounded-md p-2 bg-muted/30">
                    <input
                      type="text"
                      placeholder={t('customer.searchCustomers')}
                      value={linkQuery}
                      onChange={e => setLinkQuery(e.target.value)}
                      className="w-full px-2 py-1 text-sm rounded border bg-background"
                      autoFocus
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {linkCandidates.length === 0 ? (
                        <div className="text-xs text-muted-foreground px-2 py-1">
                          {t('customer.noLinkedCustomers')}
                        </div>
                      ) : (
                        linkCandidates.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => addLinkedCustomer(c.id)}
                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-accent flex items-center justify-between gap-2"
                          >
                            <span className="truncate">{c.name}</span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {c.id}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {linkedCustomers.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    {t('customer.noLinkedCustomers')}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {linkedCustomers.map(lc => (
                      <Badge
                        key={lc.id}
                        variant="secondary"
                        className="gap-1 pl-2 pr-1 py-1"
                      >
                        <Link
                          to={`/customer/${lc.id}`}
                          className="hover:underline"
                        >
                          {lc.name}
                        </Link>
                        {canCust.edit && (
                          <button
                            type="button"
                            onClick={() => removeLinkedCustomer(lc.id)}
                            className="ml-1 rounded hover:bg-destructive/20 p-0.5"
                            aria-label="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">{t('customer.notes')}</CardTitle>
                <input
                  ref={notesFileRef}
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const stored = await signAndPut(file, UploadKind.PDF);
                      updateCustomer(customer.id, {
                        notesAttachments: [
                          ...(customer.notesAttachments ?? []),
                          { name: file.name, ref: stored },
                        ],
                      });
                      toast(t('customer.contractUploaded'));
                    } catch (err) {
                      toast(
                        err instanceof UploadFailedError
                          ? t(err.i18nKey)
                          : t('common.error')
                      );
                    }
                    e.target.value = '';
                  }}
                />
                {canCust.edit && (
                  <Button
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => notesFileRef.current?.click()}
                  >
                    <Plus className="h-3 w-3" />
                    {t('customer.addAttachment')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                ref={notesRef}
                defaultValue={customer.notes ?? ''}
                placeholder={t('customer.addNotes')}
                readOnly={!canCust.edit}
                onBlur={e => {
                  if (!canCust.edit) return;
                  updateCustomer(customer.id, { notes: e.target.value });
                }}
              />
              {(customer.notesAttachments ?? []).map((a, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs"
                >
                  <button
                    type="button"
                    onClick={() => openUpload(a.ref)}
                    className="inline-flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <span className="truncate">{a.name}</span>
                  </button>
                  {canCust.edit && (
                    <button
                      type="button"
                      aria-label={t('common.delete')}
                      onClick={() =>
                        updateCustomer(customer.id, {
                          notesAttachments: (
                            customer.notesAttachments ?? []
                          ).filter((_, j) => j !== i),
                        })
                      }
                      className="rounded p-0.5 hover:bg-destructive/20 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {customer.legacy &&
            (customer.legacy.unmapped.length > 0 ||
              (customer.legacy.linkedFiles?.length ?? 0) > 0) && (
              <Card data-focus="legacy-import">
                <CardHeader>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left"
                    onClick={() => setLegacyOpen(v => !v)}
                    aria-expanded={legacyOpen}
                  >
                    <CardTitle className="text-sm">
                      {t('customer.legacyImport')}
                    </CardTitle>
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        legacyOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </CardHeader>
                {legacyOpen && (
                  <CardContent className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        {t('legacy.sourceSheet')}:{' '}
                      </span>
                      <span className="font-mono">
                        {customer.legacy.sourceSheet}
                      </span>
                    </div>
                    {(customer.legacy.linkedFiles?.length ?? 0) > 0 && (
                      <div className="space-y-1">
                        <div className="text-muted-foreground">
                          {t('customer.linkedFiles')}
                        </div>
                        <ul className="space-y-1">
                          {customer.legacy.linkedFiles!.map(f => (
                            <li
                              key={f}
                              className="flex items-center gap-1 font-mono text-xs break-all"
                            >
                              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {customer.legacy.unmapped.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-muted-foreground">
                          {t('legacy.unmappedFields')}
                        </div>
                        <div className="overflow-x-auto">
                          <Table className="min-w-[320px]">
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t('legacy.field')}</TableHead>
                                <TableHead>{t('legacy.value')}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {customer.legacy.unmapped.map((u, i) => (
                                <TableRow key={i}>
                                  <TableCell className="text-xs">
                                    {u.where ??
                                      u.label ??
                                      [u.row, u.col]
                                        .filter(v => v !== undefined)
                                        .join(' / ')}
                                  </TableCell>
                                  <TableCell className="text-xs break-all">
                                    {u.value}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            )}

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {t('customer.equipmentSummary')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {deviceCounts.length === 0 && (
                  <span className="text-sm text-muted-foreground">
                    {t('customer.noEquipment')}
                  </span>
                )}
                {deviceCounts.map(([k, v]) => (
                  <Badge key={k} variant={v > 0 ? 'default' : 'secondary'}>
                    {k === HOSE_COUNT_KEY
                      ? t('customer.hoses')
                      : deviceTypeLabel(k, types, lang)}
                    : {v}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extinguishers" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">
                {t('customer.extinguishers')} ({onSiteExts.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('customer.sortedByFloor')}
              </p>
            </div>
            <div className="flex gap-2">
              {/* Entry point for the first divider: with none in the list yet
                  there is no header to click and no gap to hover. */}
              {canExt.edit && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="section-add"
                  onClick={() => setInsertAt(nextSectionAnchor())}
                  disabled={customer.extinguishers.length === 0}
                >
                  <Plus className="h-4 w-4" />
                  {t('section.add')}
                </Button>
              )}
              {canMaint.edit && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="batch-inspect"
                  onClick={handleBatchInspect}
                >
                  <Calendar className="h-4 w-4" />
                  {t('customer.batchInspect')}
                </Button>
              )}
              {canExt.create && (
                <Button size="sm" asChild data-tour="add-extinguisher">
                  <Link to={`/customer/${customer.id}/extinguisher/new`}>
                    <Plus className="h-4 w-4" />
                    {t('ext.addExtinguisher')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  {sortKey === null && canExt.edit && (
                    <TableHead className="w-8" />
                  )}
                  <SortHeader
                    sortKeyProp="floor"
                    className="hidden md:table-cell"
                  >
                    {t('table.floor')}
                  </SortHeader>
                  <SortHeader sortKeyProp="location">
                    {t('table.location')}
                  </SortHeader>
                  <SortHeader sortKeyProp="mfrModel">
                    {t('table.mfrModel')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="type"
                    className="hidden sm:table-cell"
                  >
                    {t('table.type')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="agent"
                    className="hidden md:table-cell"
                  >
                    {t('table.agent')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="exactAgent"
                    className="hidden lg:table-cell"
                  >
                    {t('table.exactAgent')}
                  </SortHeader>
                  <SortHeader sortKeyProp="id" className="hidden lg:table-cell">
                    {t('table.id')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="year"
                    className="hidden sm:table-cell"
                  >
                    {t('table.year')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="last"
                    className="hidden md:table-cell"
                  >
                    {t('table.last')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="next"
                    className="hidden md:table-cell"
                  >
                    {t('table.next')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="refilled"
                    className="hidden lg:table-cell"
                  >
                    {t('table.refilled')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="billing"
                    className="hidden lg:table-cell"
                  >
                    {t('table.billing')}
                  </SortHeader>
                  <TableHead>{t('table.actions')}</TableHead>
                  <SortHeader sortKeyProp="status">
                    {t('table.status')}
                  </SortHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const renderRow = (e: Extinguisher) => {
                    const warn = ageOf(e);
                    const maintained = isMaintainedThisCycle(e);
                    // Reordering is an edit of the list (#95).
                    const draggable = sortKey === null && canExt.edit;
                    return (
                      <TableRow
                        key={e.id}
                        className={dragId === e.id ? 'opacity-40' : undefined}
                        onDragOver={
                          draggable
                            ? ev => {
                                ev.preventDefault();
                                edgeAutoScroll(ev.clientY);
                              }
                            : undefined
                        }
                        onDrop={
                          draggable
                            ? () => {
                                if (dragId) reorderExtinguisher(dragId, e.id);
                                setDragId(null);
                              }
                            : undefined
                        }
                      >
                        {draggable && (
                          <TableCell
                            className="w-8 cursor-grab text-muted-foreground"
                            draggable
                            onDragStart={() => setDragId(e.id)}
                            onDragEnd={() => setDragId(null)}
                            aria-label={t('common.dragToReorder')}
                            title={t('common.dragToReorder')}
                          >
                            <GripVertical className="h-4 w-4" />
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell">
                          {e.floor}
                        </TableCell>
                        <TableCell>{e.location}</TableCell>
                        <TableCell className="text-sm">
                          {e.mfr} {e.model}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge
                            variant="outline"
                            className="font-mono text-[10px]"
                          >
                            {deviceTypeLabel(e.type, types, lang)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs">
                          {agentTypeOf(e)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">
                          {e.content}
                        </TableCell>
                        <TableCell className="font-mono text-xs hidden lg:table-cell">
                          {e.deviceNumber}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span
                            className={`flex items-center gap-1 ${warn ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}
                            title={
                              warn
                                ? `${warn.age}y exceeds ${warn.limit}y ${warn.cls} limit`
                                : undefined
                            }
                          >
                            {e.year}
                            {warn && <AlertTriangle className="h-3 w-3" />}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {fmtDate(e.lastInspection)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {fmtDate(e.nextInspection)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {fmtDate(e.refilled)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <Badge variant="secondary">
                            {t(BILLING_LABEL_KEYS[e.billing ?? Billing.NONE])}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {/* Without Bearbeiten the device form opens
                                read-only, so the link stays as "view". */}
                            <Button
                              variant="ghost"
                              size="icon"
                              asChild
                              title={
                                canExt.edit
                                  ? t('common.edit')
                                  : t('common.view')
                              }
                            >
                              <Link
                                to={`/customer/${customer.id}/extinguisher/${e.id}`}
                                aria-label={
                                  canExt.edit
                                    ? t('common.edit')
                                    : t('common.view')
                                }
                              >
                                {canExt.edit ? (
                                  <Pencil className="h-3 w-3" />
                                ) : (
                                  <Eye className="h-3 w-3" />
                                )}
                              </Link>
                            </Button>
                            {canExt.create && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t('common.copy')}
                                aria-label={t('common.copy')}
                                onClick={() => copyExtinguisher(e)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            )}
                            {/* Replace adds the new unit and marks the old
                                one disposed: both rights. */}
                            {canExt.create && canExt.edit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t('ext.replaceDevice')}
                                aria-label={t('ext.replaceDevice')}
                                onClick={() => replaceExtinguisher(e)}
                              >
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                            )}
                            {canExt.delete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t('common.delete')}
                                aria-label={t('common.delete')}
                                onClick={() => removeExtinguisher(e)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => toggleMaintained(e)}
                            disabled={!canMaint.edit}
                            title={
                              maintained
                                ? t('ext.maintainedThisCycle')
                                : t('ext.notMaintained')
                            }
                            className="rounded p-0.5 hover:bg-muted transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                          >
                            {maintained ? (
                              <Check
                                className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                                aria-label={t('ext.maintainedThisCycle')}
                              />
                            ) : (
                              <X
                                className="h-4 w-4 text-orange-500"
                                aria-label={t('ext.notMaintained')}
                              />
                            )}
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  };

                  if (sortKey) {
                    const sorted = [...customer.extinguishers].sort((a, b) => {
                      const va = sortValue(a, sortKey);
                      const vb = sortValue(b, sortKey);
                      let cmp = 0;
                      if (typeof va === 'number' && typeof vb === 'number') {
                        cmp = va - vb;
                      } else {
                        cmp = String(va).localeCompare(String(vb));
                      }
                      return sortDir === 'asc' ? cmp : -cmp;
                    });
                    return sorted.map(renderRow);
                  }

                  // Section dividers, restored (client #30). They were removed
                  // citing #65 — but #65 is about the invoice, not this list, so
                  // the flattening was applied to the wrong screen and the
                  // technicians lost the building sections they walk by. A
                  // header renders wherever the section changes from the row
                  // above, so the dividers ride along with the manual drag
                  // order instead of regrouping it.
                  return customer.extinguishers.flatMap((e, i) => {
                    const prev = customer.extinguishers[i - 1];
                    const sec = (e.section ?? '').trim();
                    const prevSec = (prev?.section ?? '').trim();
                    const rows = [];
                    if (sec && sec !== prevSec) {
                      const count = customer.extinguishers.filter(
                        x => (x.section ?? '').trim() === sec
                      ).length;
                      const colour =
                        customer.sectionColors?.[sec] ?? SectionColor.ONE;
                      rows.push(
                        <TableRow key={`sec-${sec}-${i}`}>
                          <TableCell
                            colSpan={99}
                            className={`section-colour-${colour} py-1 px-3 font-semibold text-xs`}
                          >
                            <div className="flex items-center gap-1">
                              {!canExt.edit ? (
                                <span data-testid={`section-header-${sec}`}>
                                  {sec} ({count})
                                </span>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    data-testid={`section-header-${sec}`}
                                    onClick={() => setEditingSection(sec)}
                                    className="hover:underline"
                                    title={t('section.edit')}
                                  >
                                    {sec} ({count})
                                  </button>
                                  {/* Moving a whole section was the second half of
                                  client #30 — the dividers were editable but
                                  stuck wherever they were first created. */}
                                  <button
                                    type="button"
                                    data-testid={`section-up-${sec}`}
                                    onClick={() => moveSection(sec, -1)}
                                    aria-label={t('section.moveUp')}
                                    title={t('section.moveUp')}
                                    className="ml-1 rounded p-0.5 hover:bg-background/40"
                                  >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`section-down-${sec}`}
                                    onClick={() => moveSection(sec, 1)}
                                    aria-label={t('section.moveDown')}
                                    title={t('section.moveDown')}
                                    className="rounded p-0.5 hover:bg-background/40"
                                  >
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    } else if (i > 0 && canExt.edit) {
                      // Hover strip between two rows of the same section — the
                      // insert affordance the client sketched.
                      rows.push(
                        <TableRow
                          key={`ins-${e.id}`}
                          className="group/ins h-0 border-0"
                        >
                          <TableCell colSpan={99} className="p-0 border-0">
                            <button
                              type="button"
                              data-testid={`section-insert-${e.id}`}
                              onClick={() => setInsertAt(i)}
                              aria-label={t('section.insertHere')}
                              title={t('section.insertHere')}
                              className="flex h-3 w-full items-center justify-center opacity-40 transition-opacity hover:opacity-100 group-hover/ins:opacity-100 focus-visible:opacity-100"
                            >
                              <Plus className="h-3.5 w-3.5 text-primary" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    rows.push(renderRow(e));
                    return rows;
                  });
                })()}
                {customer.extinguishers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={99}
                      className="text-center text-muted-foreground py-8"
                    >
                      {t('customer.noExtinguishers')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          <SectionDialog
            open={editingSection !== null || insertAt !== null}
            initialName={editingSection ?? undefined}
            initialColor={
              editingSection
                ? customer.sectionColors?.[editingSection]
                : undefined
            }
            onSave={(name, colour) =>
              editingSection !== null
                ? renameSection(editingSection, name, colour)
                : insertSection(insertAt as number, name, colour)
            }
            onDelete={
              editingSection !== null
                ? () => removeSection(editingSection)
                : undefined
            }
            onCancel={() => {
              setEditingSection(null);
              setInsertAt(null);
            }}
          />
        </TabsContent>

        <TabsContent value="hoses" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">
                {t('customer.hoses')} ({customer.hoses.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('hose.hoseStandard')}
              </p>
            </div>
            {canHose.create && (
              <Button size="sm" asChild>
                <Link to={`/customer/${customer.id}/hose/new`}>
                  <Plus className="h-4 w-4" />
                  {t('hose.addHose')}
                </Link>
              </Button>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {customer.hoses.map(h => (
              <Card
                key={h.id}
                className={dragId === h.id ? 'opacity-40' : undefined}
                onDragOver={
                  canHose.edit
                    ? ev => {
                        ev.preventDefault();
                        edgeAutoScroll(ev.clientY);
                      }
                    : undefined
                }
                onDrop={
                  canHose.edit
                    ? () => {
                        if (dragId) reorderHose(dragId, h.id);
                        setDragId(null);
                      }
                    : undefined
                }
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    {/* draggable lives on the grip alone, never the Card: the
                        card contains a real <a>, which browsers drag as a URL
                        and would hijack the reorder gesture (client #84).
                        Reordering is an edit (#95). */}
                    {canHose.edit && (
                      <span
                        draggable
                        onDragStart={() => setDragId(h.id)}
                        onDragEnd={() => setDragId(null)}
                        aria-label={t('common.dragToReorder')}
                        title={t('common.dragToReorder')}
                        data-testid={`hose-grip-${h.id}`}
                        className="cursor-grab text-muted-foreground shrink-0 pt-0.5"
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold flex items-center gap-2 flex-wrap">
                        {/* Floor prefixes the location so the card reads like
                            the walk order ("1. OG Treppenhaus") — the print
                            report already does this (client #83). Legacy rows
                            carry '' rather than undefined, so filter falsy. */}
                        <span className="truncate">
                          {[h.floor, h.location].filter(Boolean).join(' ') ||
                            t('hose.fireHose')}
                        </span>
                        {h.deviceNumber && (
                          <span className="font-mono text-xs text-muted-foreground">
                            #{h.deviceNumber}
                          </span>
                        )}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {h.mfr} {h.model}{' '}
                        <span className="font-mono ml-1">{h.id}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={h.status} />
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        title={
                          canHose.edit ? t('common.edit') : t('common.view')
                        }
                      >
                        <Link
                          to={`/customer/${customer.id}/hose/${h.id}`}
                          aria-label={
                            canHose.edit ? t('common.edit') : t('common.view')
                          }
                        >
                          {canHose.edit ? (
                            <Pencil className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Link>
                      </Button>
                      {canHose.create && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          title={t('common.copy')}
                          aria-label={t('common.copy')}
                          onClick={() => copyHose(h)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      )}
                      {canHose.delete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                          onClick={async () => {
                            if (
                              await confirm(
                                `${t('common.delete')} ${t('hose.fireHose')} ${h.deviceNumber ?? h.id}?`
                              )
                            ) {
                              deleteHose(customer.id, h.id);
                              toast(t('common.delete'));
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 text-xs">
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.equipment')}
                    </div>
                    <div>
                      {t('table.year')}: {h.year}
                    </div>
                    <div>
                      {t('hose.length')}: {h.length} m
                    </div>
                    <div>Ø: {h.diameter}</div>
                    <div>Conn: {h.connection}</div>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.condition')}
                    </div>
                    {Object.entries(h.condition)
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span>
                            {t(HOSE_COND_LABEL_KEYS[k as keyof HoseCondition])}
                          </span>
                          <Badge
                            variant={
                              v === ConditionValue.OK
                                ? 'success'
                                : isConditionFault(v as ConditionValue)
                                  ? 'destructive'
                                  : 'info'
                            }
                            className="text-[10px]"
                          >
                            {/* Was printing the raw stored value ("notok").
                                With per-check findings that would read
                                "dripping" on a German screen (client #97). */}
                            {t(CONDITION_LABEL_KEYS[v as ConditionValue])}
                          </Badge>
                        </div>
                      ))}
                  </div>
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.service')}
                    </div>
                    <div>
                      {t('table.last')}: {fmtDate(h.lastService)}
                    </div>
                    <div>
                      {t('table.next')}: {fmtDate(h.nextService)}
                    </div>
                    <div>Flow: {h.flowRate}</div>
                    <div>12-bar: {h.pressureTestDate}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {customer.hoses.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('customer.noHoses')}
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                {t('customer.history')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('customer.readOnlySnapshots')}
              </p>
            </div>
            {canCust.edit && (
              <Button size="sm" onClick={handleArchive}>
                <Plus className="h-4 w-4" />
                {t('customer.createArchive')}
              </Button>
            )}
          </div>

          {/* The inspection records are the maintenance history (#95). */}
          {canMaint.read && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">
                {t('customer.inspections')}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {inspections
                  .filter(i => i.customerId === customer.id)
                  .sort((a, b) => (a.date < b.date ? 1 : -1))
                  .map(i => (
                    <Card key={i.id}>
                      <CardContent className="pt-5 space-y-1 text-sm">
                        <div className="font-semibold">{fmtDate(i.date)}</div>
                        <div className="text-muted-foreground text-xs">
                          {i.devicesServiced.length} {t('customer.devices')} —{' '}
                          {i.technician}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          {i.signatureKey && (
                            <Button
                              variant="link"
                              size="sm"
                              className="px-0 h-auto text-xs"
                              onClick={() => openUpload(i.signatureKey!)}
                            >
                              {t('inspection.viewSignature')}
                            </Button>
                          )}
                          {canInvoice && (
                            <Link
                              to={`/customer/${customer.id}/invoice?visit=${i.visitId}`}
                              className="text-xs text-primary inline-flex items-center gap-1"
                            >
                              <Receipt className="h-3 w-3" />
                              {t('inspection.invoiceVisit')}
                            </Link>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                {inspections.filter(i => i.customerId === customer.id)
                  .length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('customer.noInspections')}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {customer.archives.map(a => (
              <Card
                key={a.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => setArchiveOpen(a)}
                onKeyDown={ev => ev.key === 'Enter' && setArchiveOpen(a)}
              >
                <CardContent className="pt-5 space-y-1 text-sm">
                  <div className="font-semibold">{fmtDate(a.date)}</div>
                  <div className="text-muted-foreground text-xs">
                    {a.deviceCount} {t('customer.devices')} — {a.technician}
                  </div>
                  <Badge variant="outline" className="text-[10px] mt-1">
                    {a.id}
                  </Badge>
                </CardContent>
              </Card>
            ))}
            {customer.archives.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('customer.noArchives')}
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Opening an archive shows the register as it stood that day — every
          device with its dates and the parts used — instead of only the date
          the archive was generated (client #73). */}
      <Dialog
        open={!!archiveOpen}
        onOpenChange={v => !v && setArchiveOpen(null)}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {t('customer.archive')} —{' '}
              {archiveOpen && fmtDate(archiveOpen.date)}
            </DialogTitle>
          </DialogHeader>
          {archiveOpen && !archiveOpen.devices?.length && (
            <p className="text-sm text-muted-foreground">
              {t('customer.archiveNoSnapshot')}
            </p>
          )}
          {!!archiveOpen?.devices?.length && (
            <ScrollTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('table.id')}</TableHead>
                    <TableHead>{t('table.location')}</TableHead>
                    <TableHead>{t('ext.model')}</TableHead>
                    <TableHead>{t('table.last')}</TableHead>
                    <TableHead>{t('table.next')}</TableHead>
                    <TableHead>{t('ext.spareParts')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archiveOpen.devices.map((d: ArchiveDevice, i: number) => (
                    <TableRow key={`${d.deviceNumber ?? 'x'}-${i}`}>
                      <TableCell className="font-mono text-xs">
                        {d.deviceNumber || '—'}
                      </TableCell>
                      <TableCell>
                        {[d.floor, d.location].filter(Boolean).join(' · ')}
                      </TableCell>
                      <TableCell>
                        {[d.mfr, d.model].filter(Boolean).join(' ')}
                      </TableCell>
                      <TableCell>{fmtDate(d.lastInspection) || '—'}</TableCell>
                      <TableCell>{fmtDate(d.nextInspection) || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {(d.spareParts ?? []).join(', ') || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollTable>
          )}
        </DialogContent>
      </Dialog>

      {editing && (
        <AddressDialog
          open={true}
          onOpenChange={v => !v && setEditing(null)}
          title={
            editing === 'billingAddress'
              ? t('customer.billingAddress')
              : editing === 'objectAddress'
                ? t('customer.objectAddress')
                : t('customer.ownerAddress')
          }
          initial={customer[editing] as Address | undefined}
          onSave={saveAddress(editing)}
          onGeocode={
            editing === 'objectAddress' ? async a => geocode(a) : undefined
          }
          onApplyToObject={
            editing === 'billingAddress'
              ? a => {
                  saveAddress('billingAddress')(a);
                  saveAddress('objectAddress')(a);
                }
              : undefined
          }
        />
      )}
      {contactEdit && (
        <ContactDialog
          open
          onOpenChange={v => !v && setContactEdit(null)}
          initial={
            contactEdit.index === null
              ? undefined
              : customer.contacts?.[contactEdit.index]
          }
          onSave={saveContact(contactEdit.index)}
        />
      )}
      {completing && canMaint.create && (
        <CompleteInspectionDialog
          visit={completing}
          open
          onOpenChange={v => !v && setActiveVisit(null)}
        />
      )}
    </motion.div>
  );
}
